/**
 * Sends big MPQ blocks to a small pool of workers (block-worker.ts). Entering an area starts its
 * music and ambience; decoding those multi-megabyte ADPCM files on the main thread used to
 * freeze the game for most of a second.
 */
import { type BlockParams, decodeBlock, setLargeBlockDecoder } from '@d2/formats';

const POOL = 2;

export function installBlockDecoder(): void {
  if (typeof Worker === 'undefined') return;
  const workers: Worker[] = [];
  const pending = new Map<number, { resolve: (b: Uint8Array) => void; reject: (e: Error) => void }>();
  let next = 0;
  try {
    for (let i = 0; i < POOL; i++) {
      const w = new Worker(new URL('./block-worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e: MessageEvent<{ id: number; out?: Uint8Array; error?: string }>) => {
        const p = pending.get(e.data.id);
        if (!p) return;
        pending.delete(e.data.id);
        if (e.data.out) p.resolve(e.data.out);
        else p.reject(new Error(e.data.error ?? 'block decode failed'));
      };
      workers.push(w);
    }
  } catch {
    return;
  }
  setLargeBlockDecoder((raw: Uint8Array, params: BlockParams) => {
    const id = ++next;
    // The source may hand out views into a shared buffer: send a private copy.
    const copy = raw.slice();
    return new Promise<Uint8Array>((resolve, reject) => {
      pending.set(id, { resolve, reject });
      workers[id % workers.length].postMessage({ id, raw: copy, params }, [copy.buffer]);
    }).catch(() => decodeBlock(raw, params));
  });
}
